import { Component } from '@theme/component';

/**
 * Cart Drawer Component — compatible with Horizon 4.1.4's Component base class.
 * Handles the slide-out cart drawer: opens as modal on desktop, redirects to /cart on mobile.
 */
class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  #historyAbortController = null;
  #backdropController = null;

  connectedCallback() {
    super.connectedCallback();

    // Force close on page load
    const dialog = this.refs.dialog;
    if (dialog) {
      if (dialog.open) dialog.close();
      dialog.removeAttribute('open');
    }
    requestAnimationFrame(() => {
      const d = this.refs.dialog;
      if (d?.open) d.close();
    });

    // Listen for cart add events (try both event systems)
    document.addEventListener('cart:add', this.#handleCartAdd);
    document.addEventListener('cart-lines:update', this.#handleCartLinesUpdate);

    // Clean up history state
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:add', this.#handleCartAdd);
    document.removeEventListener('cart-lines:update', this.#handleCartLinesUpdate);
    this.#historyAbortController?.abort();
    this.#backdropController?.abort();
  }

  /**
   * Check if we're on mobile (< 750px, matching Shopify's breakpoint)
   */
  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Open the cart drawer (called by on:click="/open")
   */
  open() {
    if (this.#isMobile()) {
      window.location.href = '/cart';
      return;
    }
    this.#showDialog();
  }

  /**
   * Close the cart drawer (called by on:click="/close" or on:click="cart-drawer-component/close")
   */
  close() {
    this.#closeDialog();
  }

  #showDialog() {
    const dialog = this.refs.dialog;
    if (!dialog || dialog.open) return;

    dialog.showModal();
    document.body.style.overflow = 'hidden';

    // Update sticky state
    requestAnimationFrame(() => this.#updateStickyState());

    // Handle backdrop click to close
    this.#backdropController?.abort();
    this.#backdropController = new AbortController();
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        this.#closeDialog();
      }
    }, { signal: this.#backdropController.signal });

    // Handle Escape key (native dialog handles this, but we need cleanup)
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#closeDialog();
    }, { signal: this.#backdropController.signal });

    // History management for mobile back button
    this.#handleHistoryOpen();

    // Handle installments CTA
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#closeDialog(), { once: true });
    });
  }

  #closeDialog() {
    const dialog = this.refs.dialog;
    if (!dialog?.open) return;

    dialog.close();
    document.body.style.overflow = '';
    this.#backdropController?.abort();
    this.#handleHistoryClose();
  }

  #handleHistoryOpen() {
    if (!this.#isMobile()) return;
    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  }

  #handleHistoryClose() {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  }

  #handlePopState = () => {
    const dialog = this.refs.dialog;
    if (dialog?.open) {
      dialog.close();
      document.body.style.overflow = '';
      this.#backdropController?.abort();
    }
  };

  // Handle cart:add event (Horizon 3.5.0 style)
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !this.#isMobile()) {
      this.#showDialog();
    }
    this.#announceCartCount(event.detail?.resource?.item_count);
  };

  // Handle cart-lines:update event (Horizon 4.1.4 style)
  #handleCartLinesUpdate = (event) => {
    if (this.hasAttribute('auto-open') && event.detail?.action === 'add' && !this.#isMobile()) {
      // Wait for the cart update to complete, then open
      if (event.detail?.promise) {
        event.detail.promise.then(({ detail }) => {
          if (!detail?.didError && !this.refs.dialog?.open) {
            this.#showDialog();
          }
        }).catch(() => {});
      } else {
        this.#showDialog();
      }
    }
  };

  #announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;
    liveRegion.textContent = cartCount;
  }

  #updateStickyState() {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
